/**
 * SuggestionChip - clickable pill chip cho /chat suggestion row.
 *
 * MD-31 ASKLAW-EMBER-THEME Phase 2. Per spec F-FE-THEME-GEMINI §7.5.
 *
 * Click -> auto-fill PillInput value + focus (parent owns onClick handler).
 *
 * Visual:
 *   - pill shape (--radius-full), height 36px, padding 0 16px
 *   - bg var(--asklaw-paper-elev), border 1px var(--asklaw-border)
 *   - hover: bg --asklaw-accent-soft, border --asklaw-accent, scale 1.02 (140ms)
 *   - active: bg --asklaw-accent, color white, scale 0.98
 *
 * Animation: stagger 60ms reveal after greeting (orchestrated externally).
 *
 * Phase 2 source: 3 hard-coded VN topic + 3 EN equivalent. Phase 4+ fetch KV
 * `/api/v1/chat/suggestions` (Q5 deferred per spec).
 */

export type SuggestionChipProps = {
  text: string;
  onClick: (text: string) => void;
  ariaLabel?: string;
};

export function SuggestionChip({ text, onClick, ariaLabel }: SuggestionChipProps) {
  return (
    <button
      type="button"
      className="suggestion-chip"
      data-suggestion-chip
      onClick={() => onClick(text)}
      aria-label={ariaLabel ?? text}
    >
      <span className="suggestion-chip__text">{text}</span>
    </button>
  );
}

export type SuggestionChipRowProps = {
  lang: 'vi' | 'en';
  onPick: (text: string) => void;
};

// Phase 2 hard-coded - Phase 4+ fetch from KV
const SUGGESTIONS_VI: string[] = [
  'Hợp đồng lao động không xác định thời hạn',
  'Visa thời vụ cho expat',
  'Tranh chấp thừa kế',
];

const SUGGESTIONS_EN: string[] = [
  'Indefinite-term labor contract',
  'Seasonal visa for expats',
  'Inheritance disputes',
];

export function SuggestionChipRow({ lang, onPick }: SuggestionChipRowProps) {
  const items = lang === 'vi' ? SUGGESTIONS_VI : SUGGESTIONS_EN;
  return (
    <div
      className="suggestion-chip-row"
      role="list"
      aria-label={lang === 'vi' ? 'Gợi ý câu hỏi mẫu' : 'Suggested prompts'}
    >
      {items.map((text) => (
        <div role="listitem" key={text}>
          <SuggestionChip
            text={text}
            onClick={onPick}
            ariaLabel={
              lang === 'vi'
                ? `Dùng gợi ý: ${text}`
                : `Use suggestion: ${text}`
            }
          />
        </div>
      ))}
    </div>
  );
}

export default SuggestionChip;
